import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import PageContainer from "../components/PageContainer";
import PageTitle from "../components/PageTitle";
import CardContainer from "../components/CardContainer";
import ActivityCard from "../components/ActivityCard";
import Error from "../components/Error";
import Tabs from "../components/Tabs";
import { setFavourite, toggleFavouriteDone } from "../redux/userSlice";

export const FavouritesPage = () => {

    const { t } = useTranslation();
    const token = localStorage.getItem('ticino-wild-token');
    const dispatch = useDispatch();

    const { favourites } = useSelector(state => state.user);

    // Pending favourite: l'utente sloggato ha cliccato ❤️ su un'attività e dopo login/register
    // è stato indirizzato qui con ?addFav=<activityId>. Processo l'add una volta sola.
    const [searchParams, setSearchParams] = useSearchParams();
    const pendingAddFav = searchParams.get('addFav');
    const processedPendingRef = useRef(false);
    useEffect(() => {
        if (!pendingAddFav || processedPendingRef.current) return;
        if (!token) return;
        processedPendingRef.current = true;

        const addPendingFavourite = async () => {
            try {
                const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/set-favourite`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ activityId: pendingAddFav, isFavourite: true })
                });
                if (response.ok) {
                    const result = await response.json();
                    dispatch(setFavourite({
                        activityId: result.payload.activityId,
                        isFavourite: result.payload.isFavourite
                    }));
                }
            } catch (err) {
                console.log('Pending favourite add error:', err);
            } finally {
                // Pulisco la query string per evitare reprocessing al refresh
                setSearchParams({}, { replace: true });
            }
        };
        addPendingFavourite();
    }, [pendingAddFav, token, dispatch, setSearchParams]);

    // Estraggo solo gli activityId per la fetch (il backend si aspetta un array di stringhe)
    const favouriteIds = useMemo(
        () => (favourites || []).map(f => f.activityId),
        [favourites]
    );

    // Mando favouriteIds in backend per riceverne le attività corrispondenti
    const [favouritesData, setFavouritesData] = useState([]);
    const [isFavouritesLoading, setIsFavouritesLoading] = useState(false);
    const [favouritesError, setFavouritesError] = useState(null);

    // Transizione visiva: traccio gli activityId che stanno uscendo (fade-out prima di scomparire).
    const EXIT_TRANSITION_MS = 800;
    const [exitingIds, setExitingIds] = useState(() => new Set());

    // Diff tra favouriteIds correnti e precedenti per rilevare unfavourite (clic cuore in /favourites).
    // Se rilevo rimozioni, ritardo il refetch così la card può sfumare prima di sparire da favouritesData.
    const prevFavouriteIdsRef = useRef([]);
    useEffect(() => {
        const currentIds = favouriteIds;
        const prevIds = prevFavouriteIdsRef.current;
        const removedIds = prevIds.filter(id => !currentIds.includes(id));

        const runFetch = () => {
            if (currentIds.length > 0) {
                favouritesDataFetch();
            } else {
                setFavouritesData([]);
            }
        };

        if (removedIds.length > 0) {
            setExitingIds(prev => {
                const next = new Set(prev);
                removedIds.forEach(id => next.add(id));
                return next;
            });
            const timer = setTimeout(() => {
                setExitingIds(prev => {
                    const next = new Set(prev);
                    removedIds.forEach(id => next.delete(id));
                    return next;
                });
                runFetch();
            }, EXIT_TRANSITION_MS);
            prevFavouriteIdsRef.current = currentIds;
            return () => clearTimeout(timer);
        }

        runFetch();
        prevFavouriteIdsRef.current = currentIds;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favouriteIds.join(',')])

    const favouritesDataFetch = async () => {
        setIsFavouritesLoading(true);
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/get-user-activities-by-id-array`;
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const options = {
                method: 'POST',
                headers,
                body: JSON.stringify({ favouritesIdArray: favouriteIds })
            };
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setFavouritesData(result.payload);
                setIsFavouritesLoading(false);
            } else {
                const error = await response.json();
                throw error;
            }
        } catch (error) {
            console.error(error.message || "Errore durante la richiesta Favourites.");
            setFavouritesError(error);
            setIsFavouritesLoading(false);
        }
    }

    // Tab attiva: "todo" (Da fare) | "done" (Fatte)
    const [activeTab, setActiveTab] = useState('todo');

    // Mappa activityId → isDone per filtrare le attività
    const doneMap = useMemo(() => {
        const map = {};
        (favourites || []).forEach(f => { map[f.activityId] = f.isDone === true; });
        return map;
    }, [favourites]);

    const todoActivities = useMemo(
        () => (favouritesData || []).filter(a => !doneMap[a._id]),
        [favouritesData, doneMap]
    );
    const doneActivities = useMemo(
        () => (favouritesData || []).filter(a => doneMap[a._id]),
        [favouritesData, doneMap]
    );

    // Toggle isDone (chiamata API + dispatch Redux)
    const sendToggleDone = async (activityId, newIsDone) => {
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/toggle-favourite-done`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ activityId, isDone: newIsDone })
            });
            if (response.ok) {
                const result = await response.json();
                dispatch(toggleFavouriteDone({
                    activityId: result.payload.activityId,
                    isDone: result.payload.isDone,
                    doneAt: result.payload.doneAt
                }));
            }
        } catch (error) {
            console.log('Toggle done error: ', error);
        }
    };

    // Transizione visiva prima del cambio tab della card: la card sfuma per ~800ms,
    // poi parte la chiamata che la sposta nell'altra tab tramite Redux.
    const handleToggleWithTransition = (activityId, newIsDone) => {
        if (exitingIds.has(activityId)) return; // evita doppio click
        setExitingIds(prev => {
            const next = new Set(prev);
            next.add(activityId);
            return next;
        });
        setTimeout(() => {
            sendToggleDone(activityId, newIsDone);
            setExitingIds(prev => {
                const next = new Set(prev);
                next.delete(activityId);
                return next;
            });
        }, EXIT_TRANSITION_MS);
    };

    const tabs = [
        { value: 'todo', label: t('FavouritesPage.tabs.todo'), count: todoActivities.length },
        { value: 'done', label: t('FavouritesPage.tabs.done'), count: doneActivities.length },
    ];

    return (
        <PageContainer items="center">
            <PageTitle backLink="/" title={t('FavouritesPage.title')} />

            {/* TABS */}
            <div className="mt-4 w-full max-w-sm px-4">
                <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
            </div>

            {/* CARD CONTAINER — stessa struttura di AllActivities.
                w-full mobile: senza, con pochi preferiti dai testi corti la card
                esce stretta (vedi commento in CardContainer) */}
            <div className="w-full md:w-auto flex flex-col">
                <div className="w-full md:w-auto flex flex-col py-4">
                    {
                        !isFavouritesLoading && !favouritesError && (
                            <>
                                {activeTab === 'todo' && (
                                    todoActivities.length > 0 ? (
                                        <CardContainer>
                                            {todoActivities.map((element) => {
                                                const isExiting = exitingIds.has(element._id);
                                                return (
                                                    <div
                                                        key={element._id}
                                                        className={`h-full flex flex-col items-center gap-2 transition-opacity duration-700 ${isExiting ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                                                    >
                                                        <div className="w-full md:w-[240px] flex flex-1">
                                                            <ActivityCard activityData={element} />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            disabled={isExiting}
                                                            onClick={() => handleToggleWithTransition(element._id, true)}
                                                            className="px-3 py-1.5 rounded-xl bg-[#225424] text-white text-sm flex gap-2 items-center md:hover:opacity-90 disabled:opacity-50"
                                                        >
                                                            <i className="fi fi-rr-check mt-0.5"></i>
                                                            <span>{t('FavouritesPage.markAsDone')}</span>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </CardContainer>
                                    ) : (
                                        <div className="text-center text-neutral-500 py-8">
                                            {t('FavouritesPage.emptyTodo')}
                                        </div>
                                    )
                                )}

                                {activeTab === 'done' && (
                                    doneActivities.length > 0 ? (
                                        <CardContainer>
                                            {doneActivities.map((element) => {
                                                const isExiting = exitingIds.has(element._id);
                                                return (
                                                    <div
                                                        key={element._id}
                                                        className={`h-full flex flex-col items-center gap-2 transition-opacity duration-700 ${isExiting ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                                                    >
                                                        <div className="w-full md:w-[240px] flex flex-1 relative opacity-60">
                                                            <ActivityCard activityData={element} />
                                                            <div className="absolute top-2 right-2 bg-[#225424] text-white px-2 py-1 rounded-md text-xs font-semibold shadow z-20">
                                                                {t('FavouritesPage.doneBadge')}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            disabled={isExiting}
                                                            onClick={() => handleToggleWithTransition(element._id, false)}
                                                            className="px-3 py-1.5 rounded-xl border border-[#225424] text-[#225424] text-sm flex gap-2 items-center md:hover:bg-[#225424] md:hover:text-white disabled:opacity-50"
                                                        >
                                                            <i className="fi fi-rr-arrow-left mt-0.5"></i>
                                                            <span>{t('FavouritesPage.markAsTodo')}</span>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </CardContainer>
                                    ) : (
                                        <div className="text-center text-neutral-500 py-8">
                                            {t('FavouritesPage.emptyDone')}
                                        </div>
                                    )
                                )}
                            </>
                        )
                    }
                    {
                        favouritesError && !isFavouritesLoading &&
                        <Error />
                    }
                </div>
            </div>
        </PageContainer>
    )
}
